import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { normalizeWa } from "@/components/WhatsAppShare";
import { orderWaUrl } from "@/lib/order";
import { formatRupiah } from "@/lib/api";
import { EMPTY_DELIVERY, calcShipping, deliveryError } from "@/lib/shipping";
import { DeliveryPicker } from "@/components/menu/DeliveryPicker";

export const OrderSheet = ({ open, onOpenChange, store, cart, total }) => {
  const [customer, setCustomer] = useState({ name: "", wa: "", note: "" });
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);
  const fee = calcShipping(store, delivery);
  const set = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));
  const storeHasWa = normalizeWa(store.whatsapp || "").length >= 10;

  const send = () => {
    if (!customer.name.trim()) return toast.error("Nama wajib diisi");
    const n = normalizeWa(customer.wa);
    if (n.length < 10 || n.length > 15) return toast.error("Nomor WhatsApp tidak valid");
    const dErr = deliveryError(store, delivery);
    if (dErr) return toast.error(dErr);
    window.open(orderWaUrl(store, cart, customer, delivery), "_blank", "noopener,noreferrer");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-h-[90vh] max-w-xl overflow-y-auto rounded-t-2xl" aria-describedby={undefined} data-testid="order-sheet">
        <SheetTitle className="font-display">Pesanan Anda</SheetTitle>
        <ul className="mt-4 divide-y divide-slate-100">
          {cart.map((c) => (
            <li key={c.key} className="flex justify-between py-2 text-sm" data-testid={`order-line-${c.key}`}>
              <span>{c.name} <span className="text-slate-400">x{c.qty}</span>{c.label && <span className="block text-xs text-emerald-700">{c.label}</span>}</span>
              <span className="font-mono font-semibold">{formatRupiah(c.price * c.qty)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4">
          <DeliveryPicker store={store} value={delivery} onChange={setDelivery} />
        </div>
        <div className="mt-4 space-y-1 border-t border-slate-200 pt-3 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span className="font-mono" data-testid="order-subtotal">{formatRupiah(total)}</span></div>
          {delivery.method === "antar" && (
            <div className="flex justify-between"><span>Ongkir</span><span className="font-mono" data-testid="order-shipping">{fee === null ? "Ongkir menyusul" : formatRupiah(fee)}</span></div>
          )}
        </div>
        <div className="mt-2 flex justify-between border-t border-slate-200 pt-3">
          <span className="font-semibold">Total</span>
          <span className="font-mono text-lg font-bold text-emerald-700" data-testid="order-total">{formatRupiah(total + (fee || 0))}{fee === null ? " + ongkir" : ""}</span>
        </div>
        <div className="mt-5 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cust-name">Nama</Label>
            <Input id="cust-name" maxLength={60} value={customer.name} onChange={set("name")} data-testid="order-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-wa">No. WhatsApp (struk dikirim ke nomor ini)</Label>
            <Input id="cust-wa" inputMode="tel" maxLength={20} value={customer.wa} onChange={set("wa")} placeholder="08xxxxxxxxxx" data-testid="order-wa-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-note">Catatan (opsional)</Label>
            <Input id="cust-note" maxLength={120} value={customer.note} onChange={set("note")} placeholder="mis. tidak pedas, ambil jam 12" data-testid="order-note-input" />
          </div>
        </div>
        {!storeHasWa && (
          <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="order-store-no-wa">Toko belum mengatur nomor WhatsApp. Pesanan belum bisa dikirim.</p>
        )}
        <Button onClick={send} disabled={!storeHasWa} className="mt-5 h-12 w-full bg-[#25D366] text-base text-white hover:bg-[#1ebe5a]" data-testid="order-send-wa-button">
          <MessageCircle className="mr-2 h-5 w-5" /> Kirim Pesanan via WhatsApp
        </Button>
      </SheetContent>
    </Sheet>
  );
};
